import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { HelpCircle } from 'lucide-react';
import { api } from '../services/api';
import TotvsLogo from './TotvsLogo';
import { useAuth } from '../contexts/AuthContext';
import VersionBadge from './VersionBadge';

const ROUTE_LABELS = {
  '/dashboard':      'Dashboard',
  '/forecast':       'Forecast',
  '/new-names':      'Funil Novos',
  '/base':           'Funil Base',
  '/database':       'Banco de Dados',
  '/contas':         'Clientes de Base',
  '/esns':           'Meta ESN',
  '/esns-admin':     'Cadastro de ESNs',
  '/metas':          'Planejamento',
  '/users':          'Usuários',
  '/products':       'Produtos',
  '/import':         'Importação',
  '/sync':           'Integração ERP',
  '/agenda':         'Agenda',
  '/reports':        'Relatórios',
  '/notifications':  'Notificações',
  '/automations':    'Automações',
  '/custom-fields':  'Campos Personalizados',
  '/duplicates':     'Duplicatas',
  '/webhooks':       'Webhooks',
  '/my-area':        'Minha Área',
};

function fmt(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);
}

export default function TopBar({ onHelpToggle, helpOpen }) {
  const location = useLocation();
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);

  useEffect(() => {
    api.getDashboard()
      .then(d => setKpi(d?.kpi ?? null))
      .catch(() => null);
  }, []);

  const label = ROUTE_LABELS[location.pathname] || 'CRM';

  return (
    <header className="h-11 flex items-center px-5 border-b border-black/30 bg-[#002233] shrink-0 gap-4">

      {/* ── Módulo ativo (página atual) ── */}
      <span className="text-[13px] font-bold text-[#00DBFF] tracking-wide">{label}</span>

      <div className="flex-1" />

      {/* ── Stats inline ── */}
      {kpi && (
        <div className="hidden md:flex items-center gap-5 text-[11px]">
          <Stat dot="bg-[#00DBFF]" label="oportunidades ativas" value={kpi.leads_ativos} />
          <Stat dot="bg-[#A44DFF]" label="SaaS no mês" value={fmt(kpi.saas_mes)} />
          <Stat dot="bg-[#00FFD4]" label="contas de base" value={kpi.total_contas} />
        </div>
      )}

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* ── Version badge — apenas admin/manager ── */}
      {(user?.role === 'admin' || user?.role === 'manager' ||
        user?.role_name === 'admin' || user?.role_name === 'manager') && (
        <VersionBadge />
      )}

      {/* ── Botão de Ajuda contextual ── */}
      <button
        onClick={onHelpToggle}
        title={helpOpen ? 'Fechar ajuda' : 'Abrir ajuda desta tela'}
        className={`p-1.5 rounded transition-colors ${
          helpOpen
            ? 'text-[#00DBFF] bg-white/10'
            : 'text-white/40 hover:text-white hover:bg-white/5'
        }`}
      >
        <HelpCircle size={15} />
      </button>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* ── TOTVS | Oeste — versão branca (sem transparência, por diretrizes de marca) ── */}
      <div className="flex items-center gap-2">
        <TotvsLogo height={16} color="white" />
        <span className="text-[9px] font-semibold tracking-[0.2em] text-white/40 uppercase">| Oeste</span>
      </div>
    </header>
  );
}

function Stat({ dot, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="text-white/80 font-semibold">{value}</span>
      <span className="text-white/35">{label}</span>
    </div>
  );
}
